import { getDateLocale as getInterfaceDateLocale } from '@/utils/i18n';
import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import { format, formatDistance } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Clock, Calendar as CalendarIcon } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { generateJITSlots } from "@/lib/schedule-utils";
import { WebinarSchedule, JITSchedule } from "@/store/webinar/types";

interface AgendamentoPreviewProps {
  mode: 'scheduled' | 'jit';
  scheduledConfig?: WebinarSchedule;
  jitConfig?: JITSchedule;
}

export function AgendamentoPreview({ mode, scheduledConfig, jitConfig }: AgendamentoPreviewProps) {
  useLanguage();
  if (mode === 'scheduled' && scheduledConfig?.start_at) {
    const startDate = new Date(scheduledConfig.start_at);
    const now = new Date();
    const isValid = startDate > now;

    return (
      <Card className="h-fit">
        <CardHeader>
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <CalendarIcon className="h-4 w-4" />
            {translateInterfaceText("Sessão Agendada")}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <p className="text-sm text-muted-foreground">{translateInterfaceText("Data e Hora")}</p>
            <p className="text-lg font-semibold">
              {format(startDate, "PPP", { locale: getInterfaceDateLocale() })}
            </p>
            <p className="text-base">
              {format(startDate, "HH:mm")}
            </p>
          </div>

          <div>
            <p className="text-sm text-muted-foreground">{translateInterfaceText("Duração")}</p>
            <p className="text-base font-medium">
              {scheduledConfig.duration_min} {" "}{translateInterfaceText("minutos")}</p>
          </div>

          <div>
            <p className="text-sm text-muted-foreground">{translateInterfaceText("Fuso Horário")}</p>
            <p className="text-base font-medium">
              {scheduledConfig.timezone}
            </p>
          </div>

          {isValid && (
            <div className="pt-4 border-t">
              <p className="text-sm text-muted-foreground">{translateInterfaceText("Começa em")}</p>
              <p className="text-base font-semibold text-primary">
                {formatDistance(startDate, now, { 
                  locale: getInterfaceDateLocale(),
                  addSuffix: false 
                })}
              </p>
            </div>
          )}

          {scheduledConfig.capacity && (
            <Badge variant="secondary" className="w-full justify-center">
              {translateInterfaceText("Limite:")}{" "}{scheduledConfig.capacity} {" "}{translateInterfaceText("inscrições")}</Badge>
          )}
        </CardContent>
      </Card>
    );
  }

  if (mode === 'jit' && jitConfig) {
    const slots = generateJITSlots(jitConfig, jitConfig.display_slots);

    return (
      <Card className="h-fit">
        <CardHeader>
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Clock className="h-4 w-4" />
            {translateInterfaceText("Just-in-Time")}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <p className="text-sm text-muted-foreground mb-2">{translateInterfaceText("Próximas sessões")}</p>
            <div className="space-y-2">
              {slots.length > 0 ? (
                slots.map((slot, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-2 rounded-md bg-muted/50"
                  >
                    <span className="text-sm font-medium">
                      {format(slot, "HH:mm")}
                    </span>
                    <Badge variant="outline" className="text-xs">
                      {translateInterfaceText("Sessão")}{" "}{index + 1}
                    </Badge>
                  </div>
                ))
              ) : (
                <p className="text-sm text-muted-foreground">
                  {translateInterfaceText("Ajuste a configuração para gerar horários")}</p>
              )}
            </div>
          </div>

          <div className="pt-4 border-t space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">{translateInterfaceText("Intervalo")}</span>
              <span className="font-medium">{jitConfig.interval_min} {" "}{translateInterfaceText("min")}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">{translateInterfaceText("Horário")}</span>
              <span className="font-medium">
                {jitConfig.day_start} - {jitConfig.day_end}
              </span>
            </div>
          </div>

          <Badge variant="secondary" className="w-full justify-center">
            {translateInterfaceText("Gerado automaticamente")}</Badge>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="h-fit">
      <CardContent className="pt-6">
        <p className="text-sm text-muted-foreground text-center">
          {translateInterfaceText("Configure o agendamento para ver a prévia")}</p>
      </CardContent>
    </Card>
  );
}
